import { useCallback } from "react";
import { fetchFastApi } from "../api/fastapi";
import { useHookCacheQuery } from "../lib/hookCache";
import {
  workpaperDataSlimSchema,
  WorkpaperDataSlim,
} from "../models/audit/workpaperData";

/**
 * Read/cache hook for `/matching/match-session/{auditSessionId}/work-paper-data`,
 * registered against the shared `HookCacheProvider` under the cache key
 * `["workpaper-data", auditSessionId]`.
 *
 * Registration is the critical contract: `useSessionRunsTransitions` calls
 * `invalidate(["workpaper-data", psId])` on `queued -> ready` flips, and that
 * call is a no-op unless this hook has registered a fetcher for the matching
 * key. The cache is the single source of truth for the payload, so consumers
 * read via this hook (subscribed) and mutate via `useHookCache().setEntry`.
 *
 * Single-session API: only the active procedure session is ever read at a
 * time (per `AuditWorkPaperViewV2` rendering exactly one
 * `<ProcedureSessionWorkPaper>`), so the previous multi-id merge branch
 * was removed.
 */
export const useWorkpaperData = (auditSessionId: string | undefined) => {
  const fetcher = useCallback(async () => {
    if (!auditSessionId) {
      throw new Error("useWorkpaperData: auditSessionId is required");
    }
    const response = await fetchFastApi({
      path: `/matching/match-session/${auditSessionId}/work-paper-data`,
      method: "GET",
    });
    return workpaperDataSlimSchema.parse(response);
  }, [auditSessionId]);

  const { data, isLoading, refetch } = useHookCacheQuery<WorkpaperDataSlim>({
    key: ["workpaper-data", auditSessionId ?? ""],
    fetcher,
    enabled: !!auditSessionId,
  });

  return {
    workpaperData: data ?? null,
    loading: isLoading,
    refetch,
  };
};
